import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowRight, BookOpen, Eye, GraduationCap, List } from 'lucide-react'
import Button from '../../components/Button'
import DataTable from '../../components/DataTable'
import Drawer from '../../components/Drawer'
import IconButton from '../../components/IconButton'
import Meter from '../../components/Meter'
import StatCard from '../../components/StatCard'
import StatusBadge from '../../components/StatusBadge'
import {
  getProgramsSummary,
  listCourseExams,
  listProgramCourses,
  listPrograms,
} from './userDetailsService'
import { strings as S } from './strings'
import './userDetails.css'

const PROGRAM_TONE = { 0: 'success', 1: 'warning', 2: 'dark' }
const EXAM_TONE = { passed: 'success', failed: 'danger' }

const day = (iso) => (iso ? iso.slice(0, 10) : '')

/**
 * ملخص البرامج التدريبية — the tab the redesign had dropped entirely.
 *
 * The legacy gives it three ApexCharts radial gauges over a table, and the table
 * drills two modals deep: programme → its courses → one course's exam attempts.
 * Both are kept; both are rebuilt.
 *
 * The gauges become the app's own meter. A radial spends most of its area on
 * empty sweep and forces the eye to re-anchor on a new arc for each of the
 * three, which is exactly the reasoning that took the semi-gauges off the KPI
 * row — three bars on a common baseline compare in one glance.
 *
 * The two stacked modals become ONE sheet that walks: open a programme and it
 * shows its courses, open a course and it shows that course's attempts, with a
 * way back at the top. A modal opened over a modal has no way back except
 * closing both and starting again.
 */
export default function ProgramsTab({ userId }) {
  const [summary, setSummary] = useState(null)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  /** `{program}` shows its courses; `{program, course}` shows that course's exams. */
  const [drill, setDrill] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([getProgramsSummary(userId), listPrograms(userId)]).then(([figures, list]) => {
      if (cancelled) return
      setSummary(figures)
      setRows(list.rows)
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [userId])

  const openProgram = useCallback((program) => setDrill({ program }), [])

  const columns = useMemo(
    () => [
      { key: 'name', header: S.programs.title, render: (row) => <bdi>{row.name}</bdi> },
      {
        key: 'state',
        header: S.programs.state,
        value: (row) => S.programs.states[row.state] ?? '',
        render: (row) => (
          <StatusBadge tone={PROGRAM_TONE[row.state] ?? 'dark'}>
            {S.programs.states[row.state] ?? ''}
          </StatusBadge>
        ),
      },
      {
        key: 'details',
        header: S.programs.details,
        align: 'center',
        width: '96px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <IconButton icon={List} label={S.programs.details} onClick={() => openProgram(row)} />
        ),
      },
    ],
    [openProgram],
  )

  return (
    <div className="ud__programs">
      <div className="ud__summary">
        <StatCard
          label={S.programs.programsCount}
          value={`${summary?.successProgramsPercentage ?? 0}%`}
          hint={`${S.programs.successOf} ${summary?.totalPrograms ?? 0}`}
          icon={GraduationCap}
          tone="accent"
          loading={loading}
        >
          <Meter value={summary?.successProgramsPercentage ?? null} tone="accent" size="sm" />
        </StatCard>
        <StatCard
          label={S.programs.coursesCount}
          value={`${summary?.successCoursesPercentage ?? 0}%`}
          hint={`${S.programs.successOf} ${summary?.totalCourses ?? 0}`}
          icon={BookOpen}
          tone="info"
          loading={loading}
        >
          <Meter value={summary?.successCoursesPercentage ?? null} tone="accent" size="sm" />
        </StatCard>
        <StatCard
          label={S.programs.watchPercentage}
          value={`${summary?.coursesWatchPercentage ?? 0}%`}
          hint={S.programs.watchHint}
          icon={Eye}
          tone="violet"
          loading={loading}
        >
          <Meter value={summary?.coursesWatchPercentage ?? null} tone="accent" size="sm" />
        </StatCard>
      </div>

      <DataTable
        /* A second list on the same screen: the address already belongs to the first. */
        pageParam={null}
        title={S.programs.summary}
        columns={columns}
        rows={rows}
        loading={loading}
        getRowId={(row) => row.id}
        emptyMessage={S.programs.noPrograms}
      />

      <ProgramSheet drill={drill} userId={userId} onChange={setDrill} onClose={() => setDrill(null)} />
    </div>
  )
}

/**
 * One sheet, two depths. The head carries the way back, so the course list is
 * never something you have to close the sheet to return to.
 */
function ProgramSheet({ drill, userId, onChange, onClose }) {
  const [courses, setCourses] = useState([])
  const [exams, setExams] = useState([])
  const [loading, setLoading] = useState(false)

  const programId = drill?.program?.id
  const courseId = drill?.course?.id

  useEffect(() => {
    if (!programId) return undefined
    let cancelled = false
    setLoading(true)
    const load = courseId
      ? listCourseExams(userId, courseId).then((r) => !cancelled && setExams(r.rows))
      : listProgramCourses(userId, programId).then((r) => !cancelled && setCourses(r.rows))
    load.then(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [userId, programId, courseId])

  const openCourse = useCallback(
    (course) => onChange((current) => ({ ...current, course })),
    [onChange],
  )

  const courseColumns = useMemo(
    () => [
      { key: 'name', header: S.courses.name, render: (row) => <bdi>{row.name}</bdi> },
      { key: 'startDate', header: S.courses.startDate, render: (row) => <Day iso={row.startDate} /> },
      { key: 'endDate', header: S.courses.endDate, render: (row) => <Day iso={row.endDate} /> },
      {
        key: 'state',
        header: S.courses.state,
        value: (row) => S.courses.states[row.state] ?? S.courses.noExam,
        render: (row) =>
          row.state ? (
            <StatusBadge tone={EXAM_TONE[row.state]}>{S.courses.states[row.state]}</StatusBadge>
          ) : (
            <span className="ud__muted">{S.courses.noExam}</span>
          ),
      },
      {
        key: 'percentage',
        header: S.courses.percentage,
        render: (row) => (row.percentage == null ? '-' : `${row.percentage}%`),
      },
      { key: 'tries', header: S.courses.tries },
      {
        key: 'watchPercentage',
        header: S.courses.watchState,
        /* A percentage said as a number and as a bar. The legacy prints the
           number alone, and a column of numbers is the one place a proportion
           is hardest to compare. */
        render: (row) => (
          <span className="ud__watch">
            <Meter value={row.watchPercentage} tone="accent" size="sm" />
            <span dir="ltr">{row.watchPercentage}%</span>
          </span>
        ),
      },
      {
        key: 'details',
        header: S.programs.details,
        align: 'center',
        width: '80px',
        sortable: false,
        searchable: false,
        render: (row) =>
          row.state ? (
            <IconButton icon={Eye} label={S.exams.title} tone="info" onClick={() => openCourse(row)} />
          ) : null,
      },
    ],
    [openCourse],
  )

  const examColumns = useMemo(
    () => [
      { key: 'name', header: S.exams.name, render: (row) => <bdi>{row.name}</bdi> },
      { key: 'course', header: S.exams.course, render: (row) => <bdi>{row.course}</bdi> },
      { key: 'date', header: S.exams.date, render: (row) => <Day iso={row.date} /> },
      { key: 'percentage', header: S.exams.percentage, render: (row) => `${row.percentage}%` },
      {
        key: 'state',
        header: S.exams.state,
        value: (row) => S.courses.states[row.state] ?? '',
        render: (row) => (
          <StatusBadge tone={EXAM_TONE[row.state]}>{S.courses.states[row.state]}</StatusBadge>
        ),
      },
    ],
    [],
  )

  if (!drill) return null

  return (
    <Drawer
      dialog
      open
      size="xl"
      icon={courseId ? BookOpen : GraduationCap}
      title={courseId ? S.exams.title : S.courses.title}
      subtitle={courseId ? drill.course.name : drill.program.name}
      onClose={onClose}
    >
      {courseId && (
        <Button
          variant="quiet"
          className="ud__back"
          onClick={() => onChange({ program: drill.program })}
        >
          <ArrowRight className="rtl-flip" size={16} strokeWidth={1.75} />
          {S.exams.back}
        </Button>
      )}

      <DataTable
        /* A second list on the same screen: the address already belongs to the first. */
        pageParam={null}
        columns={courseId ? examColumns : courseColumns}
        rows={courseId ? exams : courses}
        loading={loading}
        getRowId={(row) => row.id}
        emptyMessage={courseId ? S.exams.noExams : undefined}
        /* ONE table at two depths, and the drill decides which — a filter the table cannot
           see. Opening a course from page 2 of its programme's courses asked for page 2 of
           that course's three attempts, and the pager said so while the rows loaded. */
        resetKey={courseId ?? programId}
      />
    </Drawer>
  )
}

/** A date is a Latin run whatever the page direction is. */
function Day({ iso }) {
  return (
    <span className="ud__date" dir="ltr">
      {day(iso)}
    </span>
  )
}
